import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Textarea,
  Button,
  Spinner,
  useToast,
} from "@chakra-ui/react";

// Build a tree from flat paths
const buildTree = (files) => {
  const root = {};
  files.forEach((file) => {
    const parts = file.path.split("/");
    let cur = root;
    parts.forEach((part, idx) => {
      if (!cur[part]) {
        cur[part] = {
          name: part,
          type: idx === parts.length - 1 ? "file" : "folder",
          children: {},
          fullPath: parts.slice(0, idx + 1).join("/"),
        };
      }
      cur = cur[part].children;
    });
  });
  const toArray = (node) =>
    Object.values(node).map((e) => ({
      ...e,
      children: e.type === "folder" ? toArray(e.children) : undefined,
    }));
  return toArray(root);
};

const TreeNode = ({ node, level = 0, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box pl={level * 2} py="1px">
      <Text
        fontSize="sm"
        lineHeight="1.2"
        cursor="pointer"
        onClick={() =>
          node.type === "folder"
            ? setExpanded((v) => !v)
            : onSelect(node.fullPath)
        }
        fontWeight={node.type === "folder" ? "semibold" : "normal"}
        _hover={{ textDecoration: "underline" }}
        userSelect="none"
      >
        {node.name}
      </Text>
      {expanded &&
        node.children?.map((child) => (
          <TreeNode
            key={child.fullPath}
            node={child}
            level={level + 1}
            onSelect={onSelect}
          />
        ))}
    </Box>
  );
};

const FileEditorView = () => {
  const [files, setFiles] = useState([]);
  const [tree, setTree] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://poc.mrc.ai/api/files");
      const data = await res.json();
      setFiles(data);
      setTree(buildTree(data));
    } catch (err) {
      toast({ title: "Error loading files", description: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchFileContent = async (path) => {
    setSelectedPath(path);
    try {
      const res = await fetch(
        `https://poc.mrc.ai/api/files/view?path=${encodeURIComponent(path)}`
      );
      const data = await res.json();
      setFileContent(data.content ?? "");
    } catch (err) {
      toast({ title: "Failed to load file", description: err.message, status: "error" });
    }
  };

  const saveFile = async () => {
    try {
      const res = await fetch(`https://poc.mrc.ai/api/files/save`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: selectedPath, content: fileContent }),
      });
      if (!res.ok) throw new Error("Save failed");
      toast({ title: "File saved", status: "success" });
    } catch (err) {
      toast({ title: "Failed to save file", description: err.message, status: "error" });
    }
  };

  useEffect(() => {
    // Ensure the app can actually reach the viewport edges
    // (some global styles add margins)
    document.documentElement.style.height = "100%";
    document.body.style.height = "100%";
    document.body.style.margin = "0";
    fetchFiles();
  }, []);

  if (loading) return <Spinner />;

  return (
    // Full viewport, no gaps
    <Flex h="100vh" w="100vw" overflow="hidden" gap={0}>
      {/* Left panel: file tree */}
      <Box
        w="300px"
        h="100%"
        overflowY="auto"
        borderRight="1px solid"
        borderColor="gray.200"
        p={2}
        bg="white"
      >
        {tree.map((node) => (
          <TreeNode key={node.fullPath} node={node} onSelect={fetchFileContent} />
        ))}
      </Box>

      {/* Right panel: sticky header + full-height editor */}
      <Flex flex="1" h="100%" direction="column" minW={0}>
        {/* Header bar */}
        <Flex
          align="left"
          justify="left"
          p={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          bg="white"
          gap={2}
        >
          <Button
            size="sm"
            colorScheme="blue"
            onClick={saveFile}
            isDisabled={!selectedPath}
          >
            Save
          </Button>
          <Text
            fontSize="sm"
            color="gray.600"
            noOfLines={1}
            title={selectedPath || ""}
          >
            {selectedPath ? `Editing: ${selectedPath}` : "Select a file to view/edit"}
          </Text>

        </Flex>

        {/* Editor fills the rest */}
        <Box flex="1" minH={0} overflow="hidden">
          <Textarea
            value={fileContent}
            onChange={(e) => setFileContent(e.target.value)}
            // Full height editor area
            h="100%"
            resize="auto"
            border="none"
            borderRadius={0}
            // Monospace goodness for YAML
            fontFamily="ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', Monaco, 'Courier New', monospace"
            fontSize="14px"
            lineHeight="1.4"
            whiteSpace="pre"
            spellCheck={false}
            wrap="on"
            sx={{ tabSize: 2 }} // control visible tab width
            // make the editor scroll internally
            overflow="auto"
          />
        </Box>
      </Flex>
    </Flex>
  );
};

export default FileEditorView;
